import { miniRequestWithTimeout } from './mini-timeout.js';
import { useEffect, useRef, useState } from 'react';
import type { Answer, MiniSnapshot } from '@ndrc/mini-sdk';
import { currentExercise } from '@ndrc/mini-sdk/engine';
import {
  MINI_PROTOCOL,
  requestSchema,
  validSource,
  type MiniMessage,
  type MiniRequest,
} from '@ndrc/mini-sdk/bridge';
import fr from './mini-player.fr.json' with { type: 'json' };
export interface MiniPlayerProps {
  initial: MiniSnapshot;
  storageKey: string;
  reload: () => Promise<MiniSnapshot>;
  respond: (
    revision: number,
    requestId: string,
    action: 'start' | 'draft' | 'submit',
    exerciseId?: string,
    answer?: Answer,
  ) => Promise<MiniSnapshot>;
  readOnly?: boolean;
}
interface Pending {
  revision: number;
  request: MiniRequest;
}
function answerText(
  snapshot: MiniSnapshot,
  exerciseId: string | undefined,
  answer: Answer | undefined,
): string {
  const ex = snapshot.manifest.stages.flatMap((s) => s.exercises).find((e) => e.id === exerciseId);
  const label = (id: string) =>
    [...(ex?.items ?? []), ...(ex?.targets ?? [])].find((i) => i.id === id)?.label ?? id;
  if (typeof answer === 'string') return label(answer);
  if (Array.isArray(answer)) return answer.map(label).join(' → ');
  return Object.entries(answer ?? {})
    .map(([a, b]) => `${label(a)} : ${label(b)}`)
    .join(' ; ');
}
export function MiniPlayer(props: MiniPlayerProps) {
  const frame = useRef<HTMLIFrameElement>(null),
    propsRef = useRef(props);
  useEffect(() => {
    propsRef.current = props;
  }, [props]);
  const api = useRef<{ retry: () => void; useServer: () => void; keepLocal: () => void } | null>(
    null,
  );
  const [channel] = useState(() => crypto.randomUUID());
  const [status, setStatus] = useState<MiniMessage['saveStatus']>('saved');
  const [error, setError] = useState<string | null>(null);
  const [comparison, setComparison] = useState<{
    local: string;
    server: string;
    canKeep: boolean;
  } | null>(null);
  const [frameReady, setFrameReady] = useState(false);
  const initial = props.initial;
  const origin = initial.frameUrl ? new URL(initial.frameUrl).origin : null;
  const src = initial.frameUrl
    ? `${initial.frameUrl}#${new URLSearchParams({ parentOrigin: location.origin, channel }).toString()}`
    : undefined;
  useEffect(() => {
    if (!origin) return;
    let alive = true,
      busy = false,
      latest = propsRef.current.initial,
      pending: Pending | null = null,
      conflict: MiniSnapshot | null = null,
      currentStatus: MiniMessage['saveStatus'] = 'saved',
      lastError: string | undefined;
    const key = `ndrc:mini:${props.storageKey}:${initial.versionId}`;
    const post = () => {
      if (alive && frame.current?.contentWindow)
        frame.current.contentWindow.postMessage(
          {
            protocol: MINI_PROTOCOL,
            channel,
            type: 'state',
            snapshot: {
              ...latest,
              activityStatus: propsRef.current.readOnly ? 'ended' : latest.activityStatus,
            },
            saveStatus: currentStatus,
            error: lastError,
          } satisfies MiniMessage,
          origin,
        );
    };
    const mark = (value: MiniMessage['saveStatus'], message?: string) => {
      currentStatus = value;
      lastError = message;
      if (alive) {
        setStatus(value);
        setError(message ?? null);
        post();
      }
    };
    const persist = () => {
      if (initial.preview) return;
      try {
        if (pending) localStorage.setItem(key, JSON.stringify(pending));
        else localStorage.removeItem(key);
      } catch {
        mark('unsaved', fr.storageUnavailable);
      }
    };
    const compare = (server: MiniSnapshot) => {
      conflict = server;
      const ex = currentExercise(server.manifest, server.state, server.unlockedStages);
      if (alive)
        setComparison({
          local: answerText(latest, pending?.request.exerciseId, pending?.request.answer),
          server: answerText(server, server.state.draft?.exerciseId, server.state.draft?.answer),
          canKeep: ex?.id === pending?.request.exerciseId && pending?.request.type === 'draft',
        });
      mark('conflict', fr.conflict);
    };
    const flush = async () => {
      if (busy || !pending || conflict || propsRef.current.readOnly) return;
      busy = true;
      mark('saving', lastError);
      try {
        while (pending && !conflict && alive) {
          const command = pending;
          const request = command.request;
          if (request.type === 'ready') {
            pending = null;
            break;
          }
          const result = await miniRequestWithTimeout(
            propsRef.current.respond(
              command.revision,
              request.id,
              request.type,
              request.exerciseId,
              request.answer,
            ),
            fr.connection,
          );
          latest = result;
          if (pending.request.id === request.id) pending = null;
          else pending.revision = result.revision;
          persist();
          mark(pending ? 'saving' : 'saved');
        }
      } catch (cause) {
        const code = cause && typeof cause === 'object' && 'code' in cause ? cause.code : null;
        if (code === 'CONFLICT') {
          try {
            compare(await propsRef.current.reload());
          } catch {
            mark('unsaved', fr.connection);
          }
        } else
          mark(
            'unsaved',
            code === 'NETWORK'
              ? fr.connection
              : cause instanceof Error
                ? cause.message
                : fr.connection,
          );
      } finally {
        busy = false;
      }
    };
    try {
      const text = initial.preview ? null : localStorage.getItem(key);
      if (text) {
        const value = JSON.parse(text) as { revision?: unknown; request?: unknown };
        const request = requestSchema.safeParse(value.request);
        if (
          typeof value.revision === 'number' &&
          Number.isSafeInteger(value.revision) &&
          request.success
        ) {
          pending = { revision: value.revision, request: request.data };
          {
            if (
              request.data.type === 'draft' &&
              request.data.exerciseId &&
              request.data.answer !== undefined
            )
              latest = {
                ...latest,
                state: {
                  ...latest.state,
                  draft: { exerciseId: request.data.exerciseId, answer: request.data.answer },
                },
              };
            mark('unsaved');
          }
        }
      }
    } catch {
      mark('unsaved', fr.storageUnavailable);
    }
    const receive = (event: MessageEvent) => {
      if (!validSource(event, origin, frame.current?.contentWindow ?? null)) return;
      const parsed = requestSchema.safeParse(event.data);
      if (!parsed.success || parsed.data.channel !== channel) return;
      const request = parsed.data;
      if (request.type === 'ready') {
        if (alive) setFrameReady(true);
        post();
        return;
      }
      if (propsRef.current.readOnly || conflict) {
        post();
        return;
      }
      if (pending && pending.request.type === 'submit') {
        post();
        return;
      }
      pending = { revision: latest.revision, request };
      persist();
      void flush();
    };
    window.addEventListener('message', receive);
    const retry = () => {
      void flush();
    };
    window.addEventListener('online', retry);
    const offline = () => mark('unsaved', fr.connection);
    window.addEventListener('offline', offline);
    const timer = setInterval(() => {
      if (busy || conflict) return;
      if (pending) {
        void flush();
        return;
      }
      void propsRef.current
        .reload()
        .then((value) => {
          if (alive && !busy && !pending) {
            latest = value;
            mark('saved');
          }
        })
        .catch(() => mark('unsaved', fr.connection));
    }, 5000);
    api.current = {
      retry,
      useServer() {
        if (!conflict) return;
        latest = conflict;
        conflict = null;
        pending = null;
        persist();
        setComparison(null);
        mark('saved');
      },
      keepLocal() {
        if (!conflict || !pending) return;
        latest = conflict;
        pending = {
          revision: latest.revision,
          request: { ...pending.request, id: crypto.randomUUID() },
        };
        conflict = null;
        setComparison(null);
        persist();
        void flush();
      },
    };
    post();
    if (pending && !conflict) void flush();
    return () => {
      alive = false;
      api.current = null;
      clearInterval(timer);
      window.removeEventListener('message', receive);
      window.removeEventListener('online', retry);
      window.removeEventListener('offline', offline);
    };
  }, [origin, channel, initial.activityId, initial.versionId, initial.preview, props.storageKey]);
  if (!src) return <p role="alert">{fr.notConfigured}</p>;
  return (
    <section
      className="ndrc-mini-player"
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minWidth: 0,
        height: '100%',
        minHeight: 0,
        flex: 1,
      }}
    >
      {initial.preview && <p role="status">{fr.preview}</p>}
      {!frameReady && <p role="status">{fr.loading}</p>}
      {error && <p role="alert">{error}</p>}
      {status === 'unsaved' && (
        <button className="ndrc-button" type="button" onClick={() => api.current?.retry()}>
          {fr.retry}
        </button>
      )}
      {comparison && (
        <aside className="ndrc-mini-comparison">
          <p>{fr.conflict}</p>
          <p>
            {fr.local}
            {comparison.local}
          </p>
          <p>
            {fr.server}
            {comparison.server}
          </p>
          <button className="ndrc-button" type="button" onClick={() => api.current?.useServer()}>
            {fr.useServer}
          </button>
          {comparison.canKeep && (
            <button className="ndrc-button" type="button" onClick={() => api.current?.keepLocal()}>
              {fr.keepLocal}
            </button>
          )}
        </aside>
      )}
      <iframe
        ref={frame}
        title={initial.manifest.title}
        src={src}
        sandbox="allow-scripts allow-same-origin"
        referrerPolicy="no-referrer"
        style={{ border: 0, width: '100%', height: '100%', flex: 1, minHeight: 0 }}
      />
    </section>
  );
}
